import React, { useState } from 'react';
import VideoShowcaseVideoCard from './VideoShowcaseVideoCard';
import { AnimatePresence, motion } from 'framer-motion';
import VideoShowcaseButton from './VideoShowcaseButton';
import { Pause, Play, Volume2, VolumeX, X } from 'lucide-react';
import VideoShowcaseStatusIndicator from './VideoShowcaseStatusIndicator';
import DeleteConfirmationModal from '../../../DeleteConfirmationModal';

const VideoShowcase = ({
  videos,
  handleVideoAction,
  isDeleteVideoModalOpen,
  handleDeleteVideoConfirm,
  setIsDeleteVideoModalOpen,
  videoNameToDelete,
}) => {
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isFullscreenPlaying, setIsFullscreenPlaying] = useState(false);
  const [isFullscreenMuted, setIsFullscreenMuted] = useState(true);
  const handlePlay = (videoId) => {
    setPlayingVideoId(videoId);
  };
  const handlePause = () => {
    setPlayingVideoId(null);
  };
  const handleClose = () => {
    setSelectedVideo(null);
    setIsFullscreenPlaying(false);
  };
  const toggleFullscreenPlay = () => {
    setIsFullscreenPlaying(!isFullscreenPlaying);
  };
  const toggleFullscreenMute = () => {
    setIsFullscreenMuted(!isFullscreenMuted);
  };
  const onClickPublish = (params, source) => {
    handleVideoAction(params._id, `upload to ${source}`);
  };
  return (
    <div className='space-y-8'>
      <div className='w-full'>
        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3'>
          {videos.map((video) => (
            <VideoShowcaseVideoCard
              handleVideoAction={handleVideoAction}
              key={video._id}
              video={video}
              isPlaying={playingVideoId === video._id}
              onPlay={handlePlay}
              onPause={handlePause}
              onPublish={onClickPublish}
            />
          ))}
        </div>
        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className='fixed inset-0 bg-black/90 flex items-center justify-center z-50'
            >
              <div className='relative w-full max-w-4xl aspect-video'>
                <video
                  src={selectedVideo.url}
                  className='w-full h-full object-contain rounded-lg shadow-2xl'
                  loop
                  muted={isFullscreenMuted}
                  playsInline
                  autoPlay={isFullscreenPlaying}
                />
                <div className='absolute top-4 right-4'>
                  <VideoShowcaseButton
                    variant='secondary'
                    size='sm'
                    onClick={handleClose}
                  >
                    <X className='h-4 w-4' />
                  </VideoShowcaseButton>
                </div>
                <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4'>
                  <h3 className='text-xl font-semibold text-white'>
                    {selectedVideo.title}
                  </h3>
                  <p className='text-sm text-white/80 mt-1'>
                    {selectedVideo.description}
                  </p>
                  <div className='flex justify-between items-center mt-2'>
                    <div className='flex space-x-4'>
                      <VideoShowcaseStatusIndicator
                        isPublished={selectedVideo.isPublishedOnYouTube}
                        platform='YouTube'
                      />
                      <VideoShowcaseStatusIndicator
                        isPublished={selectedVideo.isPublishedOnTikTok}
                        platform='TikTok'
                      />
                    </div>
                    <div className='flex space-x-2'>
                      <VideoShowcaseButton
                        variant='secondary'
                        size='sm'
                        onClick={toggleFullscreenPlay}
                      >
                        {isFullscreenPlaying ? (
                          <Pause size={20} />
                        ) : (
                          <Play size={20} />
                        )}
                      </VideoShowcaseButton>
                      <VideoShowcaseButton
                        variant='secondary'
                        size='sm'
                        onClick={toggleFullscreenMute}
                      >
                        {isFullscreenMuted ? (
                          <VolumeX size={20} />
                        ) : (
                          <Volume2 size={20} />
                        )}
                      </VideoShowcaseButton>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <DeleteConfirmationModal
          isOpen={isDeleteVideoModalOpen}
          onClose={() => setIsDeleteVideoModalOpen(false)}
          onConfirm={handleDeleteVideoConfirm}
          confirmationMessage={`Are you sure you want to delete the video ${videoNameToDelete}? This action
                  cannot be undone.`}
        />
      </div>
    </div>
  );
};

export default VideoShowcase;
